/**
 * «Compartir con mi cliente» (idea 9): crea un enlace público de solo lectura a la hoja del plano y lo deja
 * listo para mandar por correo o WhatsApp, con el mensaje ya redactado.
 *
 * Dos pasos:
 *   1. Crear (o elegir uno de la lista): nombre de a quién va, vigencia y si se muestran precios.
 *   2. Enviar: correo y WhatsApp del cliente, «enviarme una copia», el mensaje (editable) y los botones.
 *
 * El enlace se puede volver a copiar cuando se quiera: el servidor guarda el token cifrado y lo devuelve a quien
 * lo creó. Los enlaces de antes de eso no se pueden recuperar: se avisa y se crea otro.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy20Regular, MoreHorizontal20Regular, Send20Regular } from '@fluentui/react-icons';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Select,
  Spinner,
  Switch,
  Textarea,
  Toast,
  ToastTitle,
  useToastController,
} from '@fluentui/react-components';
import { diaDeFecha, type AvanceObraFuente, type AvisosDelEnlace, type EnlaceResumen, type OrigenAvance } from '@supernova/shared';
import {
  actualizarEnlace,
  crearEnlace,
  listarEnlaces,
  revocarEnlace,
  tokenDelEnlace,
  urlDelEnlace,
} from '../../api/enlaces.js';
import { getAvanceDePlano } from '../../api/avanceObra.js';
import { etiquetaDeDia, nombreDeLaObra } from '../../lib/avanceObra4d.js';
import { useAuthStore } from '../../stores/authStore.js';
import { GLOBAL_TOASTER_ID } from '../ThemedProvider.js';
import { EnviarDesdeMiCuenta } from '../mensajes/EnviarDesdeMiCuenta.js';
import {
  asuntoDelCorreo,
  correoValido,
  cuerpoDelCorreo,
  fechaLarga,
  telefonoParaWhatsApp,
  textoDeWhatsApp,
  urlDeCorreo,
  urlDeWhatsApp,
} from './mensajeEnlace.js';

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  projectId: string;
  fileId: string;
  /** Para el texto del mensaje. */
  nombreObra?: string;
}

const NOMBRE_DE_ORIGEN: Record<OrigenAvance, string> = {
  m1: 'OPUS Presupuesto (estimaciones)',
  m2: 'OPUS Control (avance en obra)',
};

/** Lo que quien comparte debe saber del avance (lo que el cliente verá o no). */
const TEXTO_AVISO_AVANCE: Record<string, string> = {
  sin_avances: 'Todavía no hay avances: el enlace se compartió sin ellos.',
  piezas_omitidas: 'El modelo es muy grande: tu cliente verá el porcentaje y la curva, sin las piezas.',
  demasiado_grande: 'El avance es demasiado grande para compartirlo; el enlace va sin él.',
  fuente_cambio: 'La fuente que elegiste ya no tiene avances; se usó la más al día.',
};

/** Los avisos del avance para quien comparte (el de «sin piezas» y el de «cambió la fuente» pueden venir juntos). */
const textosDeAvisoAvance = (a: AvisosDelEnlace | undefined): string[] => {
  const t: string[] = [];
  if (a?.avance && TEXTO_AVISO_AVANCE[a.avance]) t.push(TEXTO_AVISO_AVANCE[a.avance]!);
  if (a?.fuenteCambio) t.push(TEXTO_AVISO_AVANCE.fuente_cambio!);
  return t;
};

const fuenteDeValor = (v: string): { origen: OrigenAvance; obraRef: string } => {
  const [origen, ...resto] = v.split('|');
  return { origen: origen as OrigenAvance, obraRef: resto.join('|') };
};

/** El enlace con el que se está trabajando en el paso 2. */
interface ParaEnviar {
  enlace: EnlaceResumen;
  url: string;
  avisos?: AvisosDelEnlace;
}

export function CompartirConCliente({ abierto, onCerrar, projectId, fileId, nombreObra }: Props): JSX.Element {
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const user = useAuthStore((s) => s.user);
  const [nombre, setNombre] = useState('');
  const [dias, setDias] = useState(30);
  const [precios, setPrecios] = useState(false);
  // Avance de obra: fuentes que tiene este plano (null = leyendo), si se muestra y de cuál ('' = la más al día).
  const [fuentes, setFuentes] = useState<AvanceObraFuente[] | null>(null);
  const [errorAvance, setErrorAvance] = useState(false);
  const [conAvance, setConAvance] = useState(false);
  const [fuenteElegida, setFuenteElegida] = useState('');
  const [enlaces, setEnlaces] = useState<EnlaceResumen[] | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paraEnviar, setParaEnviar] = useState<ParaEnviar | null>(null);

  // Paso 2
  const [correo, setCorreo] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [copiaAMi, setCopiaAMi] = useState(true);
  const [mensajeCorreo, setMensajeCorreo] = useState('');
  const [mensajeWa, setMensajeWa] = useState('');

  const avisar = useCallback(
    (texto: string) =>
      dispatchToast(
        <Toast>
          <ToastTitle>{texto}</ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 2500 },
      ),
    [dispatchToast],
  );

  const recargar = useCallback(async () => {
    try {
      const todos = await listarEnlaces(projectId);
      setEnlaces(todos.filter((e) => e.fileId === fileId));
    } catch {
      setEnlaces([]);
    }
  }, [projectId, fileId]);

  useEffect(() => {
    if (!abierto) return;
    setParaEnviar(null);
    setError(null);
    void recargar();
    let vivo = true;
    setFuentes(null);
    setErrorAvance(false);
    setFuenteElegida('');
    getAvanceDePlano(projectId, fileId)
      .then((r) => {
        if (!vivo) return;
        setFuentes(r.fuentes);
        setConAvance(r.fuentes.length > 0);
      })
      .catch(() => {
        if (vivo) {
          setFuentes([]);
          setConAvance(false);
          setErrorAvance(true);
        }
      });
    return () => {
      vivo = false;
    };
  }, [abierto, recargar, projectId, fileId]);

  // Al abrir el paso 2 se redactan los mensajes (quedan editables).
  useEffect(() => {
    if (!paraEnviar) return;
    const d = {
      obra: paraEnviar.enlace.obra ?? nombreObra ?? 'la obra',
      cliente: paraEnviar.enlace.nombre,
      url: paraEnviar.url,
      expiraAt: paraEnviar.enlace.expiraAt,
      conPrecios: paraEnviar.enlace.mostrarPrecios,
      remitente: user?.name ?? null,
    };
    setMensajeCorreo(cuerpoDelCorreo(d));
    setMensajeWa(textoDeWhatsApp(d));
  }, [paraEnviar, nombreObra, user?.name]);

  // Una sola operación a la vez: un clic repetido (o un re-render bajo el puntero) no crea dos enlaces.
  const enCurso = useRef(false);
  const hacer = async (fn: () => Promise<void>) => {
    if (enCurso.current) return;
    enCurso.current = true;
    setTrabajando(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo completar la operación.');
    } finally {
      enCurso.current = false;
      setTrabajando(false);
    }
  };

  const crear = () =>
    hacer(async () => {
      const r = await crearEnlace(projectId, fileId, {
        expiraEnDias: dias,
        mostrarPrecios: precios,
        ...(nombre.trim() ? { nombre: nombre.trim() } : {}),
        mostrarAvance: conAvance && (fuentes?.length ?? 0) > 0,
        ...(fuenteElegida ? { avanceFuente: fuenteDeValor(fuenteElegida) } : {}),
      });
      setParaEnviar({ enlace: r.enlace, url: urlDelEnlace(r.token), avisos: r.avisos });
      setNombre('');
      await recargar();
    });

  const abrirEnvio = (e: EnlaceResumen) =>
    hacer(async () => {
      const token = await tokenDelEnlace(projectId, e.id);
      setParaEnviar({ enlace: e, url: urlDelEnlace(token) });
    });

  const copiar = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      avisar('Enlace copiado');
    } catch {
      setError('No se pudo copiar. Selecciona el enlace y cópialo a mano.');
    }
  };

  const copiarEnlaceDeLaLista = (e: EnlaceResumen) =>
    hacer(async () => {
      await copiar(urlDelEnlace(await tokenDelEnlace(projectId, e.id)));
    });

  const telefono = useMemo(() => telefonoParaWhatsApp(whatsapp), [whatsapp]);
  const correoMal = correo.trim() !== '' && !correoValido(correo);
  const whatsappMal = whatsapp.trim() !== '' && telefono === null;
  const miCorreo = user?.email ?? null;

  const sinPrecio = paraEnviar?.avisos && paraEnviar.enlace.mostrarPrecios ? paraEnviar.avisos.sinPrecio : 0;
  const porRevisar = paraEnviar?.avisos?.porRevisar ?? 0;

  return (
    <Dialog open={abierto} onOpenChange={(_, d) => !d.open && onCerrar()}>
      <DialogSurface style={{ maxWidth: 640 }}>
        <DialogBody>
          <DialogTitle>Compartir con mi cliente</DialogTitle>
          <DialogContent>
            <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
              Tu cliente verá la hoja de este plano y el plano, sin crear cuenta. Solo puede mirar: no cambia nada.
              También podrá descargar el archivo del plano completo, tal como lo tienes: trae todo su contenido (capas,
              bloques y datos de quien lo hizo), no solo lo que ves en la hoja.
            </p>

            {!paraEnviar && (
              <div style={{ display: 'grid', rowGap: 12 }}>
                <Field label="¿Para quién es? (opcional)" hint="Sirve para saludarlo en el mensaje y para reconocer este enlace en la lista.">
                  <Input value={nombre} maxLength={120} placeholder="Ing. Pérez" onChange={(_, d) => setNombre(d.value)} />
                </Field>
                <Field label="El enlace sirve durante">
                  <Select value={String(dias)} onChange={(_, d) => setDias(Number(d.value))}>
                    <option value="7">7 días</option>
                    <option value="30">30 días</option>
                    <option value="90">90 días</option>
                  </Select>
                </Field>
                <Switch
                  checked={precios}
                  onChange={(_, d) => setPrecios(d.checked)}
                  label="Mostrar precios a mi cliente"
                />
                <Switch
                  checked={conAvance && (fuentes?.length ?? 0) > 0}
                  disabled={(fuentes?.length ?? 0) === 0}
                  onChange={(_, d) => setConAvance(d.checked)}
                  label="Mostrar el avance de obra"
                />
                {fuentes !== null && fuentes.length === 0 && (
                  <p style={{ margin: 0, fontSize: 12, color: errorAvance ? 'var(--bad)' : 'var(--text-secondary)' }}>
                    {errorAvance
                      ? 'No se pudo leer el avance de obra. Cierra y vuelve a abrir esta ventana; si se crea el enlace ahora, saldrá sin avance.'
                      : 'Este plano todavía no tiene avances.'}
                  </p>
                )}
                {conAvance && fuentes && fuentes.length > 1 && (
                  <Field label="¿De dónde sale el avance?" hint="Tu cliente no ve cuál elegiste: solo ve el avance.">
                    <Select value={fuenteElegida} onChange={(_, d) => setFuenteElegida(d.value)}>
                      <option value="">La más al día</option>
                      {fuentes.map((f) => (
                        <option key={`${f.origen}|${f.obraRef}`} value={`${f.origen}|${f.obraRef}`}>
                          {NOMBRE_DE_ORIGEN[f.origen]} · {nombreDeLaObra(f.obraRef)}
                          {f.hasta ? ` · avance al ${etiquetaDeDia(diaDeFecha(f.hasta))}` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                <Button appearance="primary" disabled={trabajando} onClick={() => void crear()}>
                  {trabajando ? 'Creando…' : 'Crear enlace'}
                </Button>
              </div>
            )}

            {paraEnviar && (
              <div style={{ display: 'grid', rowGap: 12 }}>
                {sinPrecio > 0 && (
                  <p style={{ margin: 0, color: 'var(--warn)' }}>
                    {sinPrecio === 1 ? '1 concepto todavía no tiene precio' : `${sinPrecio} conceptos todavía no tienen precio`}:
                    tu cliente {sinPrecio === 1 ? 'lo verá' : 'los verá'} como «Por definir» y no entra
                    {sinPrecio === 1 ? '' : 'n'} en el total.
                  </p>
                )}
                {textosDeAvisoAvance(paraEnviar.avisos).map((t) => (
                  <p key={t} style={{ margin: 0, color: 'var(--warn)' }}>{t}</p>
                ))}
                {porRevisar > 0 && (
                  <p style={{ margin: 0, color: 'var(--warn)' }}>
                    {porRevisar === 1 ? '1 concepto tiene' : `${porRevisar} conceptos tienen`} una medición que conviene
                    revisar antes de mandar el presupuesto.
                  </p>
                )}
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                  Tu cliente verá esta obra y este plano con estos nombres:{' '}
                  <b>
                    {paraEnviar.enlace.obra ?? 'Presupuesto'} · {paraEnviar.enlace.plano ?? 'Plano'}
                  </b>
                  . Si el nombre del archivo no es para él, cámbiale el nombre al plano y pulsa «Actualizar hoja».
                </p>
                <Field label="Enlace">
                  <Input readOnly value={paraEnviar.url} onFocus={(e) => e.currentTarget.select()} />
                </Field>

                <Field
                  label="Correo de tu cliente"
                  validationState={correoMal ? 'error' : 'none'}
                  validationMessage={correoMal ? 'Escribe un correo completo, como nombre@empresa.com' : undefined}
                >
                  <Input type="email" value={correo} placeholder="cliente@empresa.com" onChange={(_, d) => setCorreo(d.value)} />
                </Field>
                {miCorreo && (
                  <Checkbox
                    checked={copiaAMi}
                    onChange={(_, d) => setCopiaAMi(Boolean(d.checked))}
                    label={`Enviarme una copia del correo (${miCorreo})`}
                  />
                )}
                <Field label="Mensaje del correo">
                  <Textarea rows={9} value={mensajeCorreo} onChange={(_, d) => setMensajeCorreo(d.value)} />
                </Field>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    appearance="primary"
                    as="a"
                    disabled={correoMal}
                    href={urlDeCorreo({
                      para: correoMal ? null : correo,
                      copia: copiaAMi ? miCorreo : null,
                      asunto: asuntoDelCorreo({ obra: paraEnviar.enlace.obra ?? nombreObra ?? 'la obra' }),
                      cuerpo: mensajeCorreo,
                    })}
                  >
                    Enviar por correo
                  </Button>
                </div>

                <Field
                  label="WhatsApp de tu cliente (con la clave de país)"
                  hint="Ejemplo: 52 55 1234 5678. Se abre WhatsApp con su chat y el mensaje escrito; tú solo pulsas enviar."
                  validationState={whatsappMal ? 'error' : 'none'}
                  validationMessage={whatsappMal ? 'Escribe el número completo, con la clave de país.' : undefined}
                >
                  <Input type="tel" value={whatsapp} placeholder="52 55 1234 5678" onChange={(_, d) => setWhatsapp(d.value)} />
                </Field>
                <Field label="Mensaje de WhatsApp">
                  <Textarea rows={6} value={mensajeWa} onChange={(_, d) => setMensajeWa(d.value)} />
                </Field>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    appearance="primary"
                    as="a"
                    disabled={whatsappMal}
                    href={urlDeWhatsApp(mensajeWa, telefono)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir en WhatsApp
                  </Button>
                  <Button onClick={() => void copiar(paraEnviar.url)}>Copiar enlace</Button>
                  <Button appearance="subtle" onClick={() => setParaEnviar(null)}>
                    Volver
                  </Button>
                </div>
                <EnviarDesdeMiCuenta
                  refEnlace={paraEnviar.enlace.id}
                  url={paraEnviar.url}
                  titulo={`Presupuesto de ${paraEnviar.enlace.obra ?? nombreObra ?? 'la obra'}`}
                  cliente={paraEnviar.enlace.nombre}
                  correo={correoMal ? '' : correo}
                  whatsapp={whatsappMal ? '' : whatsapp}
                  asunto={asuntoDelCorreo({ obra: paraEnviar.enlace.obra ?? nombreObra ?? 'la obra' })}
                  mensajeCorreo={mensajeCorreo}
                  mensajeWhatsApp={mensajeWa}
                />
              </div>
            )}

            {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}

            <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 16, paddingTop: 12 }}>
              <h4 style={{ margin: '0 0 8px' }}>
                Enlaces que ya compartiste de este plano
                {enlaces && enlaces.length > 0 ? ` (${enlaces.length})` : ''}
              </h4>
              {enlaces === null && <Spinner size="tiny" />}
              {enlaces?.length === 0 && (
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Todavía no compartiste este plano con nadie.</p>
              )}
              <div style={{ display: 'grid', rowGap: 8 }}>
                {enlaces?.map((e) => (
                  <div
                    key={e.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      alignItems: 'center',
                      columnGap: 12,
                      rowGap: 4,
                      padding: '10px 12px',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--bg-surface)',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.nombre ?? 'Sin nombre'}
                        </b>
                        <Badge appearance="tint" color={e.mostrarPrecios ? 'brand' : 'informative'} size="small">
                          {e.mostrarPrecios ? 'Con precios' : 'Sin precios'}
                        </Badge>
                        {e.avanceHasta && (
                          <Badge appearance="tint" color="success" size="small">
                            Con avance al {etiquetaDeDia(diaDeFecha(e.avanceHasta))}
                          </Badge>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        Sirve hasta el {fechaLarga(e.expiraAt)} ·{' '}
                        {e.accesos === 0
                          ? 'aún no lo abren'
                          : e.accesos === 1
                            ? 'lo abrieron 1 vez'
                            : `lo abrieron ${e.accesos} veces`}{' '}
                        · hoja del {fechaLarga(e.fotoAt)}
                      </div>
                      {!e.puedeCopiar && (
                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                          Es un enlace anterior: ya no se puede volver a copiar. Crea uno nuevo para mandarlo otra vez.
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifySelf: 'end' }}>
                      {e.puedeCopiar && (
                        <>
                          <Button
                            size="small"
                            appearance="primary"
                            icon={<Send20Regular />}
                            disabled={trabajando}
                            onClick={() => void abrirEnvio(e)}
                          >
                            Enviar
                          </Button>
                          <Button
                            size="small"
                            icon={<Copy20Regular />}
                            disabled={trabajando}
                            onClick={() => void copiarEnlaceDeLaLista(e)}
                          >
                            Copiar
                          </Button>
                        </>
                      )}
                      <Menu positioning="below-end">
                        <MenuTrigger disableButtonEnhancement>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<MoreHorizontal20Regular />}
                            aria-label="Más acciones de este enlace"
                            disabled={trabajando}
                          />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem
                              onClick={() =>
                                void hacer(async () => {
                                  const r = await actualizarEnlace(projectId, e.id);
                                  await recargar();
                                  const avisos = textosDeAvisoAvance(r.avisos).join(' ');
                                  avisar(avisos ? `Tu cliente ya ve la hoja de hoy. ${avisos}` : 'Tu cliente ya ve la hoja de hoy');
                                })
                              }
                            >
                              <div>
                                <div style={{ fontWeight: 600 }}>Actualizar la hoja</div>
                                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                                  Tu cliente verá la hoja tal como está hoy, con el mismo enlace.
                                </div>
                              </div>
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                void hacer(async () => {
                                  await revocarEnlace(projectId, e.id);
                                  await recargar();
                                  avisar('Dejaste de compartir ese enlace');
                                })
                              }
                            >
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--bad)' }}>Dejar de compartir</div>
                                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                                  El enlace deja de funcionar: quien lo abra verá que ya no está disponible.
                                </div>
                              </div>
                            </MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCerrar}>
              Cerrar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
